import { A11yModule } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  inject,
} from '@angular/core';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsScrollComponent } from '../styles/ds-scroll';

let nextContentSheetId = 0;

export type DsContentSheetShadow = 'none' | 'l2';

@Component({
  selector: 'ds-content-sheet',
  standalone: true,
  imports: [A11yModule, DsIconButtonV2Component, DsScrollComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible) {
      <div
        class="ds-content-sheet__backdrop"
        [style.top.px]="overlayTop"
        aria-hidden="true"
        (click)="onBackdropClick()"
      ></div>

      <section
        #panel
        class="ds-content-sheet__sheet"
        [class.ds-content-sheet__sheet--anchored]="isAnchored"
        [class.ds-content-sheet__sheet--shadow-none]="shadow === 'none'"
        [style.--ds-content-sheet-resolved-max-width]="resolvedMaxWidth"
        [style.--ds-content-sheet-panel-left.px]="panelLeft"
        [style.--ds-content-sheet-panel-top.px]="panelTop"
        [style.--ds-content-sheet-available-height.px]="availableHeight"
        [style.--ds-content-sheet-arrow-left.px]="arrowLeft"
        [style.--ds-content-sheet-runtime-viewport-width.px]="_viewportWidth"
        [style.--ds-content-sheet-viewport-height.px]="_viewportHeight"
        [style.--ds-content-sheet-keyboard-offset.px]="_keyboardOffset"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="title ? titleId : null"
        [attr.aria-describedby]="descriptiveText ? descriptiveTextId : null"
        [attr.aria-label]="title ? null : ariaLabel"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
      >
        @if (isAnchored) {
          <svg
            class="ds-content-sheet__arrow"
            viewBox="0 0 28 12"
            fill="none"
            aria-hidden="true"
          >
            <path d="M28 12H4.10134e-06C-0.00745699 12 10.1669 0 14.2471 0C18.3273 0 27.9935 12 28 12Z" />
          </svg>
        }

        <div class="ds-content-sheet__inner">
          <header class="ds-content-sheet__header">
            <div class="ds-content-sheet__header-center">
              <div class="ds-content-sheet__title-actions">
                @if (showBackButton) {
                  <ds-icon-button-v2
                    icon="ArrowBack"
                    size="xs"
                    emphasis="secondary-filled"
                    [ariaLabel]="backAriaLabel"
                    (clicked)="backed.emit()"
                  />
                }
                <ng-content select="[ds-content-sheet-header-left]" />
              </div>

              @if (title || descriptiveText) {
                <div class="ds-content-sheet__header-copy">
                  @if (title) {
                    <h2
                      [id]="titleId"
                      class="ds-content-sheet__title"
                      [class.ds-content-sheet__title--truncate]="truncateTitle"
                    >
                      {{ title }}
                    </h2>
                  }
                  @if (descriptiveText) {
                    <p [id]="descriptiveTextId" class="ds-content-sheet__descriptive-text">
                      {{ descriptiveText }}
                    </p>
                  }
                </div>
              }
            </div>

            <div class="ds-content-sheet__header-column ds-content-sheet__header-column--right">
              <ng-content select="[ds-content-sheet-header-right]" />
              @if (showCloseButton) {
                <ds-icon-button-v2
                  icon="Close"
                  size="s"
                  emphasis="secondary-filled"
                  [ariaLabel]="closeAriaLabel"
                  (clicked)="dismiss()"
                />
              }
            </div>

            <div class="ds-content-sheet__header-navigation">
              <ng-content select="[ds-content-sheet-header-navigation]" />
            </div>
          </header>

          <div dsScroll class="ds-content-sheet__body">
            <ng-content select=":not([ds-content-sheet-footer])" />
          </div>

          <footer class="ds-content-sheet__footer">
            <ng-content select="[ds-content-sheet-footer]" />
          </footer>
        </div>
      </section>
    }
  `,
  styles: [`
    :host { display: contents; }

    .ds-content-sheet__backdrop {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: var(--ds-content-sheet-backdrop-z-index);
      background: var(--ds-content-sheet-overlay);
      backdrop-filter: blur(var(--ds-content-sheet-backdrop-blur));
      -webkit-backdrop-filter: blur(var(--ds-content-sheet-backdrop-blur));
      animation: ds-content-sheet-backdrop-enter
        var(--ds-content-sheet-backdrop-transition-duration) ease-out;
    }

    .ds-content-sheet__sheet {
      position: fixed;
      right: 0;
      bottom: var(--ds-content-sheet-keyboard-offset, 0px);
      left: 0;
      z-index: var(--ds-content-sheet-panel-z-index);
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      width: 100%;
      max-height: calc(
        var(--ds-content-sheet-viewport-height, 100dvh) -
        var(--ds-content-sheet-max-height-offset)
      );
      border-radius:
        var(--ds-content-sheet-border-radius)
        var(--ds-content-sheet-border-radius)
        0 0;
      color: var(--ds-content-sheet-title-color);
      animation: ds-content-sheet-enter
        var(--ds-content-sheet-transition-duration) cubic-bezier(0.22, 1, 0.36, 1);
    }

    .ds-content-sheet__inner {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      box-sizing: border-box;
      width: 100%;
      min-height: 0;
      overflow: hidden;
      border-radius: inherit;
      background: var(--ds-content-sheet-background);
    }

    .ds-content-sheet__arrow {
      display: none;
      position: absolute;
      top: calc(var(--ds-content-sheet-anchored-arrow-height) * -1);
      left: var(--ds-content-sheet-arrow-left);
      width: var(--ds-content-sheet-anchored-arrow-width);
      height: var(--ds-content-sheet-anchored-arrow-height);
      fill: var(--ds-content-sheet-background);
      pointer-events: none;
    }

    .ds-content-sheet__sheet--anchored {
      top: var(--ds-content-sheet-panel-top);
      right: auto;
      bottom: auto;
      left: var(--ds-content-sheet-panel-left);
      width: calc(
        var(--ds-content-sheet-runtime-viewport-width, 100%) -
        (var(--ds-content-sheet-viewport-margin) * 2)
      );
      max-width: var(
        --ds-content-sheet-resolved-max-width,
        var(--ds-content-sheet-default-max-width)
      );
      max-height: min(
        var(--ds-content-sheet-anchored-max-height),
        var(--ds-content-sheet-available-height)
      );
      border-radius: var(--ds-content-sheet-border-radius);
      filter: drop-shadow(
        var(--ds-content-sheet-anchored-shadow-offset-x)
        var(--ds-content-sheet-anchored-shadow-offset-y)
        var(--ds-content-sheet-anchored-shadow-blur)
        var(--ds-content-sheet-anchored-shadow-color)
      );
      animation-name: ds-content-sheet-anchored-enter;
      animation-duration: var(--ds-content-sheet-anchored-transition-duration);
    }

    .ds-content-sheet__sheet--anchored .ds-content-sheet__arrow {
      display: block;
    }

    .ds-content-sheet__sheet--shadow-none {
      filter: none;
    }

    .ds-content-sheet__header {
      position: relative;
      z-index: var(--ds-content-sheet-chrome-z-index);
      display: grid;
      grid-template-columns: minmax(0, 1fr) max-content;
      align-items: flex-start;
      box-sizing: border-box;
      width: 100%;
      padding:
        var(--ds-sheet-header-padding-y)
        var(--ds-sheet-header-padding-x)
        var(--ds-sheet-header-padding-bottom);
      gap: var(--ds-sheet-header-gap);
      flex: 0 0 auto;
      background: var(--ds-content-sheet-background);
      box-shadow: var(--ds-sheet-header-shadow);
    }

    .ds-content-sheet__header-center,
    .ds-content-sheet__header-copy {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 0;
      width: 100%;
      gap: var(--ds-content-sheet-header-copy-gap);
    }

    .ds-content-sheet__header-column {
      display: flex;
      align-items: flex-start;
      min-width: 0;
      gap: var(--ds-sheet-header-gap);
    }

    .ds-content-sheet__header-column--right {
      justify-content: flex-end;
    }

    .ds-content-sheet__header-navigation {
      grid-column: 1 / -1;
      min-width: 0;
      width: 100%;
    }

    .ds-content-sheet__header-navigation:empty { display: none; }

    .ds-content-sheet__title-actions {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      width: 100%;
      gap: var(--ds-sheet-header-gap);
    }

    .ds-content-sheet__title-actions:empty { display: none; }

    .ds-content-sheet__title,
    .ds-content-sheet__descriptive-text {
      width: 100%;
      margin: 0;
      text-align: left;
      overflow-wrap: anywhere;
    }

    .ds-content-sheet__title {
      color: var(--ds-content-sheet-title-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-content-sheet-title-font-size);
      font-weight: var(--ds-content-sheet-title-font-weight);
      line-height: var(--ds-content-sheet-title-line-height);
    }

    .ds-content-sheet__title--truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-content-sheet__descriptive-text {
      color: var(--ds-content-sheet-descriptive-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-content-sheet-descriptive-text-font-size);
      font-weight: var(--ds-content-sheet-descriptive-text-font-weight);
      line-height: var(--ds-content-sheet-descriptive-text-line-height);
      white-space: pre-line;
    }

    .ds-content-sheet__body {
      position: relative;
      z-index: var(--ds-content-sheet-content-z-index);
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      box-sizing: border-box;
      width: 100%;
      min-height: 0;
      padding:
        var(--ds-sheet-body-padding-top)
        var(--ds-content-sheet-slot-padding)
        var(--ds-sheet-body-padding-y);
      gap: var(--ds-content-sheet-slot-gap);
      overflow-y: auto;
      background: var(--ds-content-sheet-background);
      color: var(--ds-content-sheet-title-color);
    }

    .ds-content-sheet__body ::ng-deep h1,
    .ds-content-sheet__body ::ng-deep h2,
    .ds-content-sheet__body ::ng-deep h3,
    .ds-content-sheet__body ::ng-deep h4,
    .ds-content-sheet__body ::ng-deep h5,
    .ds-content-sheet__body ::ng-deep h6,
    .ds-content-sheet__body ::ng-deep p {
      margin: 0;
    }

    .ds-content-sheet__footer {
      position: relative;
      z-index: var(--ds-content-sheet-chrome-z-index);
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      box-sizing: border-box;
      width: 100%;
      padding:
        var(--ds-content-sheet-footer-padding-top)
        var(--ds-content-sheet-footer-padding)
        var(--ds-content-sheet-footer-padding);
      gap: var(--ds-content-sheet-footer-gap);
      background: var(--ds-content-sheet-background);
    }

    .ds-content-sheet__footer:empty { display: none; }

    .ds-content-sheet__inner:not(:has(> .ds-content-sheet__footer:not(:empty)))
      > .ds-content-sheet__body {
      padding-bottom: var(--ds-content-sheet-slot-padding);
    }

    .ds-content-sheet__footer ::ng-deep [ds-content-sheet-footer] {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: var(--ds-content-sheet-footer-gap);
    }

    .ds-content-sheet__footer ::ng-deep ds-button-v2 .ds-btn {
      width: 100%;
      max-width: none;
    }

    @keyframes ds-content-sheet-backdrop-enter {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes ds-content-sheet-enter {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }

    @keyframes ds-content-sheet-modal-enter {
      from { opacity: 0; transform: translate(-50%, -48%); }
      to { opacity: 1; transform: translate(-50%, -50%); }
    }

    @keyframes ds-content-sheet-anchored-enter {
      from {
        opacity: 0;
        transform: translateY(calc(var(--ds-content-sheet-anchored-gap) * -1));
      }
      to { opacity: 1; transform: translateY(0); }
    }

    @media (min-width: 840px) {
      .ds-content-sheet__sheet:not(.ds-content-sheet__sheet--anchored) {
        top: 50%;
        right: auto;
        bottom: auto;
        left: 50%;
        width: calc(100% - (var(--ds-content-sheet-viewport-margin) * 2));
        max-width: var(
          --ds-content-sheet-resolved-max-width,
          var(--ds-content-sheet-default-max-width)
        );
        border-radius: var(--ds-content-sheet-border-radius);
        transform: translate(-50%, -50%);
        animation-name: ds-content-sheet-modal-enter;
      }

      .ds-content-sheet__sheet:not(.ds-content-sheet__sheet--anchored) .ds-content-sheet__footer {
        align-items: flex-end;
      }

      .ds-content-sheet__sheet:not(.ds-content-sheet__sheet--anchored)
        .ds-content-sheet__footer ::ng-deep [ds-content-sheet-footer] {
        width: auto;
        flex-direction: row;
        justify-content: flex-end;
      }

      .ds-content-sheet__sheet:not(.ds-content-sheet__sheet--anchored)
        .ds-content-sheet__footer ::ng-deep ds-button-v2 .ds-btn {
        width: auto;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-content-sheet__backdrop,
      .ds-content-sheet__sheet { animation: none; }
    }
  `],
})
export class DsContentSheetComponent implements OnChanges, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private viewportCleanup?: () => void;
  private scheduledFrame?: number;
  private resizeObserver?: ResizeObserver;
  private dismissingInternally = false;

  @Input({ transform: booleanAttribute }) visible = false;
  @Input() title = '';
  @Input() descriptiveText = '';
  @Input() ariaLabel = 'Content sheet';
  @Input() anchor: HTMLElement | null = null;
  @Input() overlayBoundary: HTMLElement | null = null;
  @Input({ transform: booleanAttribute }) dismissOnBackdropClick = true;
  @Input({ transform: booleanAttribute }) showCloseButton = true;
  @Input({ transform: booleanAttribute }) showBackButton = false;
  @Input({ transform: booleanAttribute }) truncateTitle = false;
  @Input() shadow: DsContentSheetShadow = 'l2';
  @Input() closeAriaLabel = 'Cerrar';
  @Input() backAriaLabel = 'Volver';
  @Input() set maxWidth(value: number | string) {
    this.resolvedMaxWidth = this.toCssLength(value, 'var(--ds-content-sheet-default-max-width)');
    if (this.visible) this.schedulePositionUpdate();
  }

  @Output() dismissed = new EventEmitter<void>();
  @Output() backed = new EventEmitter<void>();

  @ViewChild('panel') private panel?: ElementRef<HTMLElement>;

  readonly titleId = `ds-content-sheet-title-${nextContentSheetId}`;
  readonly descriptiveTextId = `ds-content-sheet-description-${nextContentSheetId++}`;

  _viewportHeight: number | null = null;
  _viewportWidth: number | null = null;
  _keyboardOffset: number | null = null;
  resolvedMaxWidth = 'var(--ds-content-sheet-default-max-width)';
  panelLeft = 0;
  panelTop = 0;
  arrowLeft = 0;
  overlayTop = 0;
  availableHeight = 0;

  get isAnchored(): boolean { return this.anchor !== null; }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.startTracking();
        this.schedulePositionUpdate();
      } else {
        this.stopTracking();
        // Closing this sheet always counts as a dismissal, even when some
        // other action outside the sheet (e.g. a sibling header item) forced
        // `visible` closed — the app relies on `dismissed` to know the panel
        // is gone regardless of what caused the close. Internal dismiss()
        // calls already emitted, so skip the duplicate.
        if (changes['visible'].previousValue === true && !this.dismissingInternally) {
          this.dismissed.emit();
        }
        this.dismissingInternally = false;
      }
      return;
    }

    if (this.visible && (changes['anchor'] || changes['overlayBoundary'])) {
      this.observeResizeTargets();
      this.schedulePositionUpdate();
    }
  }

  ngOnDestroy(): void { this.stopTracking(); }

  dismiss(): void {
    this.dismissingInternally = true;
    this.dismissed.emit();
  }

  onBackdropClick(): void {
    if (this.dismissOnBackdropClick) this.dismiss();
  }

  private readonly onViewportChange = (): void => {
    this.updateViewport();
    this.schedulePositionUpdate();
  };

  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (this.visible && event.key === 'Escape') {
      event.preventDefault();
      this.dismiss();
    }
  };

  private startTracking(): void {
    this.stopTracking();
    if (typeof window === 'undefined') return;

    this.updateViewport();
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.addEventListener('scroll', this.onViewportChange, { passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', this.onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', this.onViewportChange, { passive: true });
    document.addEventListener('keydown', this.onKeydown);
    this.observeResizeTargets();

    this.viewportCleanup = () => {
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onViewportChange, true);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
      document.removeEventListener('keydown', this.onKeydown);
    };
  }

  private stopTracking(): void {
    if (this.scheduledFrame !== undefined && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.scheduledFrame);
      this.scheduledFrame = undefined;
    }
    this.viewportCleanup?.();
    this.viewportCleanup = undefined;
    this.resizeObserver?.disconnect();
    this._viewportHeight = null;
    this._viewportWidth = null;
    this._keyboardOffset = null;
  }

  private updateViewport(): void {
    if (!this.visible || typeof window === 'undefined') return;
    const visualViewport = window.visualViewport;
    const layoutViewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = visualViewport?.height ?? window.innerHeight;
    const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
    this._viewportWidth = layoutViewportWidth;
    this._viewportHeight = viewportHeight;
    this._keyboardOffset = Math.max(0, window.innerHeight - viewportHeight - viewportOffsetTop);
    this.cdr.markForCheck();
  }

  private observeResizeTargets(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver ??= new ResizeObserver(this.onViewportChange);
    this.resizeObserver.disconnect();
    const targets = new Set(
      [this.anchor, this.overlayBoundary].filter(
        (target): target is HTMLElement => target !== null,
      ),
    );
    targets.forEach((target) => this.resizeObserver?.observe(target));
  }

  private schedulePositionUpdate(): void {
    if (!this.visible || typeof requestAnimationFrame === 'undefined') return;
    if (this.scheduledFrame !== undefined) cancelAnimationFrame(this.scheduledFrame);
    // Two frames: on the panel's very first open, the newly mounted subtree
    // (anchor button, header, projected content) can still be settling its
    // layout in the frame right after creation. A single rAF sometimes reads
    // that pre-settled geometry, under-measuring the available height until
    // the sheet is closed and reopened. Waiting one extra frame lets that
    // layout finish before we measure it.
    this.scheduledFrame = requestAnimationFrame(() => {
      this.scheduledFrame = requestAnimationFrame(() => {
        this.scheduledFrame = undefined;
        this.updatePosition();
      });
    });
  }

  private updatePosition(): void {
    const panel = this.panel?.nativeElement;
    const anchor = this.anchor;
    if (!panel || !anchor || typeof window === 'undefined') {
      this.overlayTop = 0;
      return;
    }

    const panelStyle = getComputedStyle(panel);
    const panelWidth = panel.getBoundingClientRect().width;
    const anchorRect = anchor.getBoundingClientRect();
    const boundaryRect = this.overlayBoundary?.getBoundingClientRect() ?? anchorRect;
    const margin = this.cssNumber(panelStyle, '--ds-content-sheet-viewport-margin');
    const gap = this.cssNumber(panelStyle, '--ds-content-sheet-anchored-gap');
    const radius = this.cssNumber(panelStyle, '--ds-content-sheet-border-radius');
    const arrowWidth = this.cssNumber(panelStyle, '--ds-content-sheet-anchored-arrow-width');
    const viewportWidth = this._viewportWidth
      ?? (document.documentElement.clientWidth || window.innerWidth);
    const idealLeft = anchorRect.right - panelWidth;
    const maxLeft = Math.max(margin, viewportWidth - margin - panelWidth);
    const left = Math.min(Math.max(idealLeft, margin), maxLeft);
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const minArrowLeft = radius;
    const maxArrowLeft = Math.max(minArrowLeft, panelWidth - radius - arrowWidth);

    this.panelLeft = left;
    this.panelTop = boundaryRect.bottom + gap;
    this.overlayTop = boundaryRect.bottom;
    this.availableHeight = Math.max(0, window.innerHeight - this.panelTop - margin);
    this.arrowLeft = Math.min(
      Math.max(anchorCenter - left - arrowWidth / 2, minArrowLeft),
      maxArrowLeft,
    );
    this.cdr.markForCheck();
  }

  private cssNumber(style: CSSStyleDeclaration, property: string): number {
    const value = Number.parseFloat(style.getPropertyValue(property));
    return Number.isFinite(value) ? value : 0;
  }

  private toCssLength(value: number | string | null | undefined, fallback: string): string {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string' && value.trim().length > 0) {
      const trimmed = value.trim();
      return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
    }
    return fallback;
  }
}
